/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login if user is not authenticated.
 * Validates org slug from URL and syncs with OrganizationContext.
 */

import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, hasSession, isInitialized, signOut } = useAuth();
  const { organizations, currentOrganization, setCurrentOrganization, hasInitialLoad, error: orgError } = useOrganization();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const location = useLocation();

  // Debug logging
  console.log('[ProtectedRoute] State:', { hasSession, isAuthenticated, isInitialized, hasInitialLoad, orgCount: organizations.length });

  // Sync URL slug with current organization
  useEffect(() => {
    if (!orgSlug || !hasInitialLoad || organizations.length === 0) return;

    const orgFromSlug = organizations.find(o => o.slug === orgSlug);
    if (orgFromSlug && currentOrganization?.id !== orgFromSlug.id) {
      setCurrentOrganization(orgFromSlug.id);
    }
  }, [orgSlug, organizations, currentOrganization, setCurrentOrganization, hasInitialLoad]);

  // Show loading while auth is being checked
  // hasSession === null means still checking localStorage
  // hasSession === true but !isInitialized means localStorage has session, waiting for Supabase confirmation
  if (hasSession === null || (hasSession === true && !isInitialized)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if definitely not authenticated
  // Only redirect when: hasSession is false OR (auth is initialized AND no session)
  if (!hasSession || (isInitialized && !isAuthenticated)) {
    console.log('[ProtectedRoute] REDIRECTING TO LOGIN:', { hasSession, isAuthenticated, isInitialized });
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait for org loading to complete before rendering app
  // This prevents race conditions where orgs haven't loaded yet
  if (!hasInitialLoad) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading organizations...</p>
        </div>
      </div>
    );
  }

  // If org loading is done but we have no orgs, show error (don't auto-signout)
  // User can manually sign out if needed - auto-signout causes race condition issues
  if (organizations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 max-w-md text-center p-6">
          <h2 className="text-xl font-semibold text-foreground">No Organization Access</h2>
          <p className="text-sm text-muted-foreground">
            {orgError || 'You don\'t have access to any organizations. Please contact your administrator.'}
          </p>
          <button
            onClick={() => signOut()}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // If we have an orgSlug, validate user has access (only when orgs are loaded)
  if (orgSlug && hasInitialLoad && organizations.length > 0) {
    const org = organizations.find(o => o.slug === orgSlug);
    if (!org) {
      // User doesn't have access to this org - redirect to their default
      const defaultOrg = currentOrganization || organizations[0];
      if (defaultOrg?.slug) {
        return <Navigate to={`/${defaultOrg.slug}`} replace />;
      }
    }
  }

  // Render children or Outlet for nested routes
  return children ? <>{children}</> : <Outlet />;
}
